// frontend_react/src/components/paymodal/index.jsx

import React from 'react';
import '../../styles/paymodal.css';

import { useCheckoutState } from './useCheckoutState';
import { formatEUR }        from './constants';

import WizardStepper    from './WizardStepper';
import OrderSummary     from './OrderSummary';
import DiscountManager  from './DiscountManager';
import VentilationPanel from './VentilationPanel';
import PaymentSummary   from './PaymentSummary';
import PaymentSelector  from './PaymentSelector';
import TicketPanel      from './TicketPanel';
import AdminModal       from './AdminModal';
import CashModal        from './CashModal';

// ─── CheckoutWizard ───────────────────────────────────────────────────────────

const CheckoutWizard = ({
    onClose,
    receiptUuid  = null,
    initialItems = null,  // articles du panier passés directement depuis CartContext
    onPaid       = null,
    pdfFormat    = 'A4',
    checkFormat  = true,
}) => {
    const state = useCheckoutState({ receiptUuid, initialItems, onPaid, pdfFormat, checkFormat });

    const {
        step, handleNext, handleBack, canGoBack,
        isFooterDisabled, footerLabel, footerAmount,

        isApiMode, loading, apiError,

        orderItems, globalDiscounts, totals,
        handleUpdateQty, handleRemoveItemDiscount,
        handleRemoveGlobalDiscount, handleApplyTicketTotal, handleApplyItemDiscount,
        handleApplyDiscount, receiptLines,

        ventilationMode, setVentilationMode, availableVentModes,
        splitCount, setSplitCount, splitBaseAmount, splitPaymentsMade,
        freeAmount, setFreeAmount,
        partialQty, setPartialQty, unpaidItems,

        paidAmount, remainingAmount, processing,
        paymentHistory, selectedMethod, handleSelectMethod,
        printReceipt, setPrintReceipt,
        ventilationTarget, handleValidatePayment,

        cashModalOpen, handleCashValidate, handleCashClose,
        cashPaymentLog,

        fullReceipt, pdfUrl,

        closeLocked, adminModalOpen, adminModalPurpose,
        handleRequestCancel, handleAdminSuccess, handleAdminClose,
    } = state;

    // ── Fermeture ──────────────────────────────────────────────────────────────
    const handleCloseClick = () => {
        if (closeLocked) {
            handleRequestCancel();
        } else {
            onClose?.();
        }
    };

    // ── Footer ─────────────────────────────────────────────────────────────────
    const handleFooterClick = () => {
        if (step === 1 || step === 2) handleNext();
        if (step === 3) handleValidatePayment();
    };

    // ── Résumé lecture seule (étapes 2-4) ──────────────────────────────────────
    const readonlyOrder = (
        <OrderSummary
            items={orderItems}
            totals={totals}
            globalDiscounts={globalDiscounts}
            isEditable={false}
        />
    );

    // ── Chargement initial ─────────────────────────────────────────────────────
    if (loading && step === 1) {
        return (
            <>
                <div className="modal-close-bar">
                    <button className="modal-close-btn" onClick={() => onClose?.()}>← Fermer</button>
                </div>
                <div className="pay-modal-window" style={{ alignItems: 'center', justifyContent: 'center' }}>
                    <p className="text-muted" style={{ padding: 40 }}>Chargement du ticket…</p>
                </div>
            </>
        );
    }

    return (
        <>
            {/* ── Barre fermeture ── */}
            <div className="modal-close-bar">
                {step === 4 ? (
                    <button className="modal-close-btn" onClick={() => onClose?.()}>← Fermer</button>
                ) : (
                    <button
                        className={`modal-close-btn${closeLocked ? ' modal-close-btn--locked' : ''}`}
                        onClick={handleCloseClick}
                        title={closeLocked ? 'Paiement en cours — annulation admin requise' : 'Fermer'}
                    >
                        {closeLocked ? '🔒 Annuler la session' : '← Fermer'}
                    </button>
                )}
            </div>

            {/* ── Fenêtre principale ── */}
            <div className="pay-modal-window">
                <WizardStepper currentStep={step} />

                {/* Bandeau erreur API */}
                {apiError && (
                    <div style={{
                        background: 'rgba(255,53,97,0.12)',
                        borderBottom: '1px solid rgba(255,53,97,0.3)',
                        padding: '8px 16px',
                        fontSize: 13,
                        color: 'var(--action)',
                        flexShrink: 0,
                    }}>
                        ⚠ {apiError}
                    </div>
                )}

                <div className="wizard-content">

                    {/* ── Étape 1 : Récap + Remises ── */}
                    {step === 1 && (
                        <div className="layout-2-cols">
                            <OrderSummary
                                items={orderItems}
                                totals={totals}
                                globalDiscounts={globalDiscounts}
                                isEditable={!isApiMode}
                                canRemoveDiscounts={true}
                                onUpdateQty={handleUpdateQty}
                                onRemoveItemDiscount={handleRemoveItemDiscount}
                                onRemoveGlobalDiscount={handleRemoveGlobalDiscount}
                            />
                            <DiscountManager
                                orderItems={orderItems}
                                receiptLines={receiptLines}
                                onApplyDiscount={handleApplyDiscount}
                                disabled={processing}
                            />
                        </div>
                    )}

                    {/* ── Étape 2 : Ventilation ── */}
                    {step === 2 && (
                        <div className="layout-2-cols">
                            {readonlyOrder}
                            <VentilationPanel
                                mode={ventilationMode}
                                setMode={setVentilationMode}
                                availableModes={availableVentModes}
                                orderItems={orderItems}
                                remainingAmount={remainingAmount}
                                unpaidItems={unpaidItems}
                                splitCount={splitCount}
                                setSplitCount={setSplitCount}
                                splitBaseAmount={splitBaseAmount}
                                splitPaymentsMade={splitPaymentsMade}
                                freeAmount={freeAmount}
                                setFreeAmount={setFreeAmount}
                                partialQty={partialQty}
                                setPartialQty={setPartialQty}
                            />
                        </div>
                    )}

                    {/* ── Étape 3 : Paiement (3 colonnes) ── */}
                    {step === 3 && (
                        <div className="layout-3-cols">
                            {readonlyOrder}
                            <PaymentSummary
                                ventilationTarget={ventilationTarget}
                                totalTTC={totals.totalTTC}
                                paidAmount={paidAmount}
                                paymentHistory={paymentHistory}
                            />
                            <PaymentSelector
                                selectedMethod={selectedMethod}
                                onSelectMethod={handleSelectMethod}
                                printReceipt={printReceipt}
                                setPrintReceipt={setPrintReceipt}
                                disabled={processing}
                            />
                        </div>
                    )}

                    {/* ── Étape 4 : Ticket ── */}
                    {step === 4 && (
                        <div className="layout-2-cols">
                            {readonlyOrder}
                            <TicketPanel
                                totals={totals}
                                orderItems={orderItems}
                                globalDiscounts={globalDiscounts}
                                paymentHistory={paymentHistory}
                                cashPaymentLog={cashPaymentLog}
                                fullReceipt={fullReceipt}
                                pdfUrl={pdfUrl}
                                onFinish={() => {
                                    onPaid?.({ receiptUuid });
                                    onClose?.();
                                }}
                                onFinishWithTicket={() => {
                                    if (!isApiMode) console.log('Impression ticket…');
                                    onPaid?.({ receiptUuid });
                                    onClose?.();
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* ── Footer ── */}
                {step !== 4 && (
                    <div className="wizard-footer">
                        <div className="footer-side footer-side--left">
                            {canGoBack && (
                                <button
                                    className="back-step-btn"
                                    onClick={handleBack}
                                    disabled={processing}
                                >
                                    ← Retour
                                </button>
                            )}
                        </div>

                        <button
                            className={[
                                'next-step-btn',
                                isFooterDisabled ? 'next-step-btn--disabled' : '',
                            ].join(' ')}
                            onClick={handleFooterClick}
                            disabled={isFooterDisabled}
                        >
                            {processing ? 'Traitement…' : footerLabel()}
                            {!processing && footerAmount() != null && (
                                <span className="footer-amount">
                                    {formatEUR(footerAmount())}
                                </span>
                            )}
                        </button>

                        <div className="footer-side footer-side--right" />
                    </div>
                )}
            </div>

            {/* ── Modale admin ── */}
            {adminModalOpen && (
                <AdminModal
                    purpose={adminModalPurpose}
                    onSuccess={handleAdminSuccess}
                    onClose={handleAdminClose}
                />
            )}

            {/* ── Modale espèces ── */}
            <CashModal
                open={cashModalOpen}
                remaining={ventilationTarget || remainingAmount}
                onClose={handleCashClose}
                onValidate={handleCashValidate}
            />
        </>
    );
};

// ─── Wrapper overlay ─────────────────────────────────────────────────────────

const PayModal = (props) => (
    <div className="pay-modal-overlay">
        <CheckoutWizard {...props} />
    </div>
);

export default PayModal;